/**
 * useDesktopRingNotification.ts — PHONE-A5 (V-07, 2026-09-02).
 *
 * ─── WHAT WAS WRONG ───────────────────────────────────────────────────────
 *
 * Nothing about an incoming call could reach a person who was not looking at
 * the tab. The ring is a banner drawn inside the app and a tone played through
 * the Web Audio API — both of which a background tab is allowed to suppress,
 * and neither of which exists at all if the window is behind another one. Web
 * push was removed on 2026-08-18 and there is no `new Notification(` anywhere
 * in the frontend, so a colleague working in a spreadsheet with the app open in
 * another window had, functionally, no phone.
 *
 * ─── WHY THE NOTIFICATIONS API AND NOT WEB PUSH ───────────────────────────
 *
 * Web push means a service worker, a VAPID key pair, a subscription store, and
 * a server-side delivery path — and it exists to reach a browser that has the
 * page CLOSED. That is not this problem. This is a page that is open and
 * running, in a window that is not on top. `new Notification()` from a live
 * page needs none of that infrastructure and cannot go stale.
 *
 * ─── THE RULES THIS HOOK OBEYS ────────────────────────────────────────────
 *
 *  · ONLY when the app cannot be seen. If the tab is visible AND the window has
 *    focus, the in-app banner is already doing the job and an OS notification
 *    on top of it is noise.
 *  · NEVER asks for permission. `Notification.requestPermission()` fires a
 *    browser prompt, and a prompt that appears unprompted on page load is the
 *    single most-blocked pattern on the web — Chrome and Firefox both punish
 *    it, and a user who dismisses it once cannot easily be asked again. It is
 *    requested from a BUTTON in Settings → Phones and nowhere else.
 *  · One notification per call. `tag` is the call sid, so a re-raise of the
 *    same ring REPLACES rather than stacking; `renotify` is deliberately not
 *    set, so the replacement is silent.
 *  · `requireInteraction` so it does not evaporate after a few seconds — a
 *    ring that disappears before you look up is the bug, restated.
 *  · Closed the moment the banner clears, for any reason: answered here,
 *    answered by a colleague, declined, or rung out. An OS notification for a
 *    call that is already over is worse than none.
 */
import { useEffect, useRef } from 'react';

export interface DesktopRingBanner {
  callSid: string;
  fromNumber: string;
  fromName?: string | null;
}

export interface DesktopAlertConditions {
  /** `Notification.permission`, or null where the API does not exist. */
  permission: NotificationPermission | null;
  /** `document.visibilityState === 'visible'`. */
  documentVisible: boolean;
  /** `document.hasFocus()`. A visible tab in a background WINDOW still fails
   *  this, which is the case that matters most: the app is on screen, just not
   *  the thing being looked at. */
  windowFocused: boolean;
  /** The account-level `comms.call_incoming_desktop` preference. */
  preferenceEnabled: boolean;
}

/**
 * The whole decision, extracted so it can be tested without a DOM, a timer or
 * a Twilio Device. Every one of these conditions has been the reason a
 * notification either failed to appear or appeared when it should not have.
 */
export function shouldRaiseDesktopAlert(c: DesktopAlertConditions): boolean {
  if (c.permission !== 'granted') return false;
  if (!c.preferenceEnabled) return false;
  // Visible AND focused means the banner is on screen in front of the person.
  return !(c.documentVisible && c.windowFocused);
}

/** The line a person reads on the notification: their name if we know it, the
 *  number if we do not. Never "unknown" — a number is information. */
export function desktopAlertTitle(banner: DesktopRingBanner): string {
  return `Incoming call · ${banner.fromName || banner.fromNumber || 'unknown number'}`;
}

export interface DesktopRingOptions {
  /** The account-level preference. Defaults to true so a missing/failed
   *  preference read never silences the phone — the browser permission is
   *  already an explicit per-device opt-in. */
  preferenceEnabled?: boolean;
  /** Called when the notification itself is clicked and the ring is still up. */
  onAccept?: () => void;
}

export function useDesktopRingNotification(
  banner: DesktopRingBanner | null,
  opts: DesktopRingOptions = {},
): void {
  const { preferenceEnabled = true, onAccept } = opts;
  const openRef = useRef<Notification | null>(null);
  // The click handler is created once per notification but must call the
  // LATEST accept callback — TwilioContext re-creates it on most renders.
  const acceptRef = useRef<(() => void) | undefined>(onAccept);
  acceptRef.current = onAccept;
  const bannerRef = useRef<DesktopRingBanner | null>(banner);
  bannerRef.current = banner;

  useEffect(() => {
    const close = () => {
      try { openRef.current?.close(); } catch { /* already dismissed */ }
      openRef.current = null;
    };

    if (!banner) {
      close();
      return undefined;
    }

    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') return undefined;

    const raise = shouldRaiseDesktopAlert({
      permission: window.Notification.permission,
      documentVisible: document.visibilityState === 'visible',
      windowFocused: typeof document.hasFocus === 'function' ? document.hasFocus() : true,
      preferenceEnabled,
    });
    if (!raise) return undefined;

    let notification: Notification;
    try {
      notification = new window.Notification(desktopAlertTitle(banner), {
        body: 'Click to answer in the app.',
        // The sid, so a second raise of the SAME ring replaces this one rather
        // than stacking a second alert for one call.
        tag: banner.callSid,
        requireInteraction: true,
        silent: false,
      });
    } catch {
      // Some browsers throw here rather than resolving a rejected permission
      // (and every browser throws on a page that is not a secure context).
      // A missing OS notification must never break the in-app ring.
      return undefined;
    }

    openRef.current = notification;
    notification.onclick = () => {
      try { window.focus(); } catch { /* pop-up policy; the click still counts */ }
      try { notification.close(); } catch { /* already gone */ }
      openRef.current = null;
      // Only if the call is still ringing. Clicking a notification for a call
      // that a colleague already took must not try to answer it.
      if (bannerRef.current) acceptRef.current?.();
    };

    return close;
    // `preferenceEnabled` is intentionally in the deps: turning the preference
    // off mid-ring should close an alert that is already on screen.
  }, [banner, preferenceEnabled]);

  // Nothing may outlive the provider.
  useEffect(() => () => {
    try { openRef.current?.close(); } catch { /* already dismissed */ }
    openRef.current = null;
  }, []);
}
